/**
 * User Counts Context
 * Manages real-time counts for likes, following, etc.
 * Updates across all components when user actions occur
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { listingsAPI, ordersAPI, usersAPI } from '@/lib/api';

const UserCountsContext = createContext(null);

export const UserCountsProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [counts, setCounts] = useState({
        participating: 0,
        buying: 0,
        sold: 0,
        forSale: 0,
        favorites: 0,  // Now represents likes count
        following: 0,
        myAuctions: 0  // My active auctions (not sold yet)
    });
    const [loading, setLoading] = useState(false);

    // Fetch all counts from API
    const fetchCounts = useCallback(async () => {
        if (!isAuthenticated) return;
        
        setLoading(true);
        try {
            const [bidsRes, buyOrdersRes, sellOrdersRes, likesRes, myListingsRes, followingRes, myAuctionsRes] = await Promise.all([
                listingsAPI.getMyBids().catch(() => ({ data: [] })),
                ordersAPI.getAll('buyer').catch(() => ({ data: [] })),
                ordersAPI.getAll('seller').catch(() => ({ data: [] })),
                listingsAPI.getMyLikes().catch(() => ({ data: [] })),  // Changed from watchlist to likes
                listingsAPI.getMy().catch(() => ({ data: [] })),
                usersAPI.getFollowedSellers().catch(() => ({ data: [] })),
                listingsAPI.getMyAuctions().catch(() => ({ data: [] }))
            ]);
            
            const myListings = Array.isArray(myListingsRes.data) ? myListingsRes.data : [];
            const sellingOrders = Array.isArray(sellOrdersRes.data) ? sellOrdersRes.data : [];
            const myAuctions = Array.isArray(myAuctionsRes.data) ? myAuctionsRes.data : [];
            
            // Calculate sold count: only non-finalized sales
            const finalizedStatuses = ['completed', 'cancelled'];
            const nonFinalizedSoldCount = myListings.filter(l => {
                if (l.status !== 'sold') return false;
                const order = sellingOrders.find(o => o.listing_id === l.listing_id);
                if (!order) return true;
                return !finalizedStatuses.includes(order.order_status);
            }).length;
            
            // Count active auctions (not sold, not expired)
            const activeAuctionsCount = myAuctions.filter(a => 
                a.status === 'active' || (a.status !== 'sold' && a.status !== 'cancelled')
            ).length;
            
            setCounts({
                participating: Array.isArray(bidsRes.data) ? bidsRes.data.length : 0,
                buying: Array.isArray(buyOrdersRes.data) ? buyOrdersRes.data.length : 0,
                favorites: Array.isArray(likesRes.data) ? likesRes.data.length : 0,  // Now counts likes
                forSale: myListings.filter(l => l.status === 'active' && l.listing_type !== 'auction').length,
                sold: nonFinalizedSoldCount,
                following: Array.isArray(followingRes.data) ? followingRes.data.length : 0,
                myAuctions: activeAuctionsCount
            });
        } catch (error) {
            console.error('Error fetching user counts:', error);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    // Initial fetch
    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    // Increment a specific count
    const incrementCount = useCallback((key) => {
        setCounts(prev => ({
            ...prev,
            [key]: (prev[key] || 0) + 1
        }));
    }, []);

    // Decrement a specific count
    const decrementCount = useCallback((key) => {
        setCounts(prev => ({
            ...prev,
            [key]: Math.max(0, (prev[key] || 0) - 1)
        }));
    }, []);

    // Set a specific count
    const setCount = useCallback((key, value) => {
        setCounts(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    return (
        <UserCountsContext.Provider value={{
            counts,
            loading,
            fetchCounts,
            incrementCount,
            decrementCount,
            setCount
        }}>
            {children}
        </UserCountsContext.Provider>
    );
};

export const useUserCounts = () => {
    const context = useContext(UserCountsContext);
    if (!context) {
        // Return a fallback for components outside the provider
        return {
            counts: { participating: 0, buying: 0, sold: 0, forSale: 0, favorites: 0, following: 0 },
            loading: false,
            fetchCounts: () => {},
            incrementCount: () => {},
            decrementCount: () => {},
            setCount: () => {}
        };
    }
    return context;
};

export default UserCountsContext;
